import { useState, useCallback, useMemo, useEffect } from 'react';
import { Calendar, Globe, Trophy, TrendingUp, Activity, Search } from 'lucide-react';
import useRailFilters from './useRailFilters';
import { getLeagueById } from '../../utils/footballLeagueUtils';

const PRIMARY_LEAGUE_LABELS = {
  '39': 'Premier League',
  '140': 'La Liga',
  '78': 'Bundesliga',
  '135': 'Serie A',
  '61': 'Ligue 1',
  '71': 'Brasileirão'
};

/**
 * useFootballRailFilters - Football-specific rail filter configuration
 * Provides tabs, filters, and handlers for football dashboard
 * 
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {string} initialDate - Initial date value
 */
const useFootballRailFilters = ({ onFiltersChange, initialDate }) => {
  // Initialize activeTab from URL params to prevent ping-pong on mount
  const getInitialActiveTab = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const leagueFromUrl = params.get('league');
      // If URL has a league that matches a primary tab, use it
      if (leagueFromUrl && leagueFromUrl !== 'all') {
        return String(leagueFromUrl);
      }
    }
    return 'all';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showLeagueSheet, setShowLeagueSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [showMarketSheet, setShowMarketSheet] = useState(false);
  
  // Default filter values - use provided initial date or today
  const defaultFilters = useMemo(() => {
    let dateStr = initialDate;
    
    if (!dateStr) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Debug: Log default filter initialization with date handling
    // console.log('🔧 [useFootballRailFilters] defaultFilters created with initialDate:', initialDate, 'resulting date:', dateStr);
    
    return {
      date: dateStr,
      league: 'all',
      market: 'all',
      sort: 'smart'
    };
  }, []); // Empty deps - capture initial values only once!
  
  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'football',
    defaultFilters,
    onFiltersChange
  });
  
  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return 'Select Date';
    
    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return 'Select Date';
    }
    
    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);
    
    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);
  
  // Format country for display
  const formatCountryLabel = useCallback((country) => {
    if (!country || country === 'all') return 'All Countries';
    
    const countryFlags = {
      'BR': '🇧🇷 Brazil',
      'GB': '🏴󐁧󐁢󐁥󐁮󐁧󐁿 England',
      'ES': '🇪🇸 Spain',
      'DE': '🇩🇪 Germany',
      'IT': '🇮🇹 Italy',
      'FR': '🇫🇷 France'
    };
    
    return countryFlags[country] || country;
  }, []);
  
  // Format league for display
  const formatLeagueLabel = useCallback((league) => {
    if (!league || league === 'all') return 'All Leagues';

    const normalizedId = typeof league === 'object' && league !== null
      ? String(league.id || '')
      : String(league);

    if (!normalizedId || normalizedId === 'all') return 'All Leagues';

    if (PRIMARY_LEAGUE_LABELS[normalizedId]) {
      return PRIMARY_LEAGUE_LABELS[normalizedId];
    }

    const leagueObj = getLeagueById(Number(normalizedId));
    return leagueObj?.name || normalizedId;
  }, [getLeagueById]);
  
  // Primary tabs configuration - Football leagues only
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Matches' },
      { id: '71', label: 'Brasileirão' },
      { id: '39', label: 'Premier League' },
      { id: '140', label: 'La Liga' },
      { id: '135', label: 'Serie A' },
      { id: '78', label: 'Bundesliga' },
      { id: '61', label: 'Ligue 1' },
      { id: 'browse', label: 'Browse...'}
    ];
  }, []);
  
  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    const configs = [
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true
      }
    ];
    
    // Only show league chip if "All Matches" is selected
    //if (activeTab === 'all') {
      configs.push({
        id: 'league',
        type: 'select',
        getLabel: formatLeagueLabel,
        hasDropdown: true
      });
    //}
    
    // Add market and sort chips
    configs.push(
      {
        id: 'market',
        type: 'select',
        label: filters.market === 'live' ? 'Live' : 
               filters.market === 'prematch' ? 'Pre-match' : 
               'Markets',
        hasDropdown: true
      },
      {
        id: 'sort',
        type: 'select',
        label: filters.sort === 'time' ? 'Time' :
               filters.sort === 'league' ? 'League' :
               filters.sort === 'smart' ? 'Smart' :
               'Sort',
        hasDropdown: true
      }
    );
    
    return configs;
  }, [filters.market, filters.sort, activeTab, formatDateLabel, formatCountryLabel, formatLeagueLabel]);
  
  // Build filter objects for SecondaryRail
  const filterObjects = useMemo(() => 
    buildFilterObjects(filterConfig, filters),
    [filterConfig, filters, buildFilterObjects]
  );
  
  // Handle filter chip click
  const handleFilterClick = useCallback((filterId, filterData) => {
    switch (filterId) {
      case 'date':
        setShowDateSheet(true);
        break;
      case 'league':
        setShowLeagueSheet(true);
        break;
      case 'market':
        setShowMarketSheet(true);
        break;
      case 'sort':
        setShowSortSheet(true);
        break;
      default:
        break;
    }
  }, [filters.market, updateFilter]);
  
  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    if (tabId === 'browse') {
      // Open league sheet for browsing
      setShowLeagueSheet(true);
    } else {
      setActiveTab(tabId);
      
      // If selecting a league directly, update filter
      if (tabId !== 'all') {
        // Keep as string to maintain consistency
        updateFilter('league', tabId);
      } else {
        updateFilter('league', 'all');
      }
    }
  }, [updateFilter]);
  
  // Simple league-to-tab sync without complex ref tracking
  // Only sync when league changes to a primary tab value
  useEffect(() => {
    // Find if current league matches a primary tab
    const matchingTab = primaryTabs.find(tab => 
      tab.id === String(filters.league)
    );
    
    // Only update activeTab if:
    // 1. We have a matching primary tab AND
    // 2. The activeTab isn't already set to it AND
    // 3. We're not in browse mode
    if (matchingTab && activeTab !== matchingTab.id && activeTab !== 'browse') {
      setActiveTab(matchingTab.id);
    }
    // If league is 'all' and we're not already on 'all' tab, sync it
    else if (filters.league === 'all' && activeTab !== 'all' && activeTab !== 'browse') {
      setActiveTab('all');
    }
    // Don't force tab changes for non-primary leagues - let user stay on current tab
  }, [filters.league, activeTab, primaryTabs]); // Include all dependencies
  
  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,
    
    // Tabs and filters
    primaryTabs,
    filterObjects,
    
    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    
    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showLeagueSheet,
    setShowLeagueSheet,
    showSortSheet,
    setShowSortSheet,
    showMarketSheet,
    setShowMarketSheet
  };
};

export default useFootballRailFilters;
